/* eslint-disable prettier/prettier */
import { get } from 'lodash'
import moment from 'moment'
import type { Project_NewFormat } from './Project_NewFormat'
import type { Project_OldFormat } from './Project_OldFormat'
import {
  ProjectTypeFederal,
  ProjectTypePrivate,
  ProjectTypePublic,
} from './ProjectType'

export function mapNewProjectStructureToOld(
  project: Project_NewFormat,
): Project_OldFormat {
  try {
    const invoiceProject = project

    // const external_project_id = get(invoiceProject, 'external_project_id')
    // const name = get(invoiceProject, 'name')
    const address = get(invoiceProject, 'address')
    const contractValue = get(invoiceProject, 'contractValue.$numberDecimal')
    const startDate = get(invoiceProject, 'startDate')
    const endDate = get(invoiceProject, 'endDate')
    const jobId = get(invoiceProject, 'jobId')
    const role = get(invoiceProject, 'role')
    const type = get(invoiceProject, 'type')

    // jobAddress
    const jobAddress = get(invoiceProject, 'jobAddress')
    const jobAddress_address = get(jobAddress, 'address')
    const jobAddress_unitNo = get(jobAddress, 'unitNo')
    const jobAddress_state = get(jobAddress, 'state')
    const jobAddress_zipCode = get(jobAddress, 'zipCode')
    const jobAddress_city = get(jobAddress, 'city')
    const jobAddress_lotNumber = get(jobAddress, 'lotNumber')
    const jobAddress_blockNumber = get(jobAddress, 'blockNumber')

    // primeContractorDetails
    const primeContractorDetails = get(invoiceProject, 'primeContractorDetails')
    const primeContractorDetails_businessName = get(
      primeContractorDetails,
      'businessName',
    )
    const primeContractorDetails_firstName = get(
      primeContractorDetails,
      'firstName',
    )
    const primeContractorDetails_lastName = get(
      primeContractorDetails,
      'lastName',
    )
    const primeContractorDetails_businessPhoneNumber = get(
      primeContractorDetails,
      'businessPhoneNumber',
    )
    const primeContractorDetails_businessEmail = get(
      primeContractorDetails,
      'businessEmail',
    )
    const primeContractorDetails_businessAddress = get(
      primeContractorDetails,
      'businessAddress',
    )
    const primeContractorDetails_state = get(primeContractorDetails, 'state')
    const primeContractorDetails_city = get(primeContractorDetails, 'city')
    const primeContractorDetails_zipCode = get(
      primeContractorDetails,
      'zipCode',
    )

    // privateProjectDetails
    const privateProjectDetails = get(invoiceProject, 'privateProjectDetails')
    // const privateProjectDetails_privateProjectType = get(privateProjectDetails,'privateProjectType')
    const privateProjectDetails_builtFor_buildForType = get(
      privateProjectDetails,
      'builtFor.buildForType',
    )
    // const privateProjectDetails_builtFor_notes = get(privateProjectDetails, 'builtFor.notes')

    // publicProjectDetails
    const publicProjectDetails = get(invoiceProject, 'publicProjectDetails')
    const publicProjectDetails_description = get(
      publicProjectDetails,
      'description',
    )
    const publicProjectDetails_hasBond = get(publicProjectDetails, 'hasBond')
    // const publicProjectDetails_isBondRequired = get(publicProjectDetails, 'isBondRequired')

    //federalProjectDetails
    const federalProjectDetails_hasBond = get(
      invoiceProject,
      'federalProjectDetails.hasBond',
    )

    const individualOwners = get(invoiceProject, 'individualOwners') ?? [] // [] {firstName, lastName, phone, homeAddress, state, city, zipCode}
    const businessOwners = get(invoiceProject, 'businessOwners') ?? [] // [] {businessName, firstName, lastName, businessPhoneNumber, businessAddress, state, city, zipCode}

    let projectType: unknown = {
      group: 'projectType',
      items: [],
    }

    if (type === 'Private') {
      const projectTypePrivate: ProjectTypePrivate = {
        group: 'projectType',
        items: [
          {
            identifier: 'type',
            title: 'Type',
            content: 'private',
            filled: true,
          },
          {
            identifier: 'privateType',
            title: 'Select type of private project',
            content: 'commercial',
            filled: true,
          },
          {
            identifier: 'projectBuiltFor',
            title: 'Project is being built for:',
            content: privateProjectDetails_builtFor_buildForType ?? '',
            filled: !!privateProjectDetails_builtFor_buildForType,
          },
        ],
      }

      projectType = projectTypePrivate
    }

    if (type === 'Public') {
      const projectTypePublic: ProjectTypePublic = {
        group: 'projectType',
        items: [
          {
            identifier: 'type',
            title: 'Type',
            content: 'public',
            filled: true,
          },
          {
            identifier: 'descriptionOfPublicProject',
            title: 'Select type of private project',
            content: publicProjectDetails_description ?? '',
            filled: !!publicProjectDetails_description,
          },
          {
            identifier: 'bondExists',
            title: 'Is there a bond for this project?',
            content: publicProjectDetails_hasBond ? 'yes' : 'no',
            filled: true,
          },
          {
            identifier: 'uploadBond',
            title: 'Please upload a copy of the bond',
            content: '',
            filled: false,
          },
        ],
      }

      projectType = projectTypePublic
    }

    if (type === 'Federal') {
      const projectTypeFederal: ProjectTypeFederal = {
        group: 'projectType',
        items: [
          {
            identifier: 'type',
            title: 'Type',
            content: 'federal',
            filled: true,
          },
          {
            identifier: 'bondExists',
            title: 'Is there a bond for this project?',
            content: federalProjectDetails_hasBond ? 'yes' : 'no',
            filled: true,
          },
          {
            identifier: 'uploadBond',
            title: 'Please upload a copy of the bond',
            content: '',
            filled: false,
          },
        ],
      }
      projectType = projectTypeFederal
    }

    const oldStructure: Project_OldFormat = {
      address: address ?? 'inital project',
      data: {
        propertyAddress: {
          group: 'propertyAddress',
          items: [
            {
              identifier: 'address',
              title: 'Address',
              content: {
                streetAddress: jobAddress_address || '',
                state: jobAddress_state || '',
                zip: jobAddress_zipCode || '',
                city: jobAddress_city || '',
                unitNum: jobAddress_unitNo || '',
                houseNum: '',
                subdivision: '',
                lotNum: jobAddress_lotNumber || '',
                blockNum: jobAddress_blockNumber || '',
              },
              filled: !!(
                jobAddress_address ||
                jobAddress_state ||
                jobAddress_zipCode ||
                jobAddress_city ||
                jobAddress_unitNo ||
                jobAddress_lotNumber ||
                jobAddress_blockNumber
              ),
            },
            {
              identifier: 'jobId',
              title: 'What is the Job ID?',
              content: jobId || '',
              filled: !!jobId,
            },
            {
              identifier: 'noticeOfCommencement',
              title: 'Please upload a notice of commencement',
              content: '',
              filled: false,
            },
          ],
        },
        projectInformation: {
          group: 'projectInformation',
          items: [
            {
              identifier: 'contractValue',
              title: 'Contract value',
              content: contractValue || '',
              filled: !!contractValue,
            },
            {
              identifier: 'uploadContract',
              title: 'Please upload the contract',
              content: '',
              filled: false,
            },
            {
              identifier: 'timePeriod',
              title: '',
              content: {
                firstDayOnJob: startDate
                  ? moment(startDate).format('MM/DD/YYYY')
                  : '',
                expectedLastDayOnJob: endDate
                  ? moment(endDate).format('MM/DD/YYYY')
                  : '',
              },
              filled: !!(startDate || endDate),
            },
            {
              identifier: 'role',
              title: 'Role on this project',
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              content: role || '',
              filled: !!role,
            },
            {
              identifier: 'primeContractorDetails',
              title: 'Tell us more about the prime contractor',
              content: {
                businessName: primeContractorDetails_businessName || '',
                firstName: primeContractorDetails_firstName || '',
                lastName: primeContractorDetails_lastName || '',
                phone: primeContractorDetails_businessPhoneNumber || '',
                email: primeContractorDetails_businessEmail || '',
                businessAddress: {
                  address: primeContractorDetails_businessAddress || '',
                  city: primeContractorDetails_city || '',
                  state: primeContractorDetails_state || '',
                  zip: primeContractorDetails_zipCode || '',
                },
              },
              filled: !!(
                primeContractorDetails_businessName ||
                primeContractorDetails_firstName ||
                primeContractorDetails_lastName ||
                primeContractorDetails_businessPhoneNumber ||
                primeContractorDetails_businessEmail ||
                primeContractorDetails_businessAddress ||
                primeContractorDetails_city ||
                primeContractorDetails_state ||
                primeContractorDetails_zipCode
              ),
            },
          ],
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        projectType,
        propertyOwner: {
          group: 'propertyOwner',
          items: [
            {
              identifier: 'details',
              title: 'Details',
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              content: [
                ...individualOwners.map((owner) => ({
                  ...owner,
                  type: 'individual',
                })),
                ...businessOwners.map((owner) => ({
                  ...owner,
                  type: 'business',
                  phone: owner.businessPhoneNumber,
                  address: owner.businessAddress,
                })),
              ],
              filled: !!(individualOwners?.length || businessOwners?.length),
            },
          ],
        },
      },
    }

    return oldStructure
  } catch (err) {
    console.error(err)
    return {
      address: 'inital project',
    }
  }
}
